import { useEffect } from "react";
import { Navigate, Outlet, useParams } from "react-router";
import { toast } from "sonner";
import { useClaimSetWritable } from "@/hooks/use-claim-set-writable";
import { useClaimStrategy } from "@/hooks/use-claim-strategy";
import { tabPath } from "../stage-vocabulary";

/**
 * What the attorney is told when the stage is shut: the server's own words,
 * repeated so the toast needs no round trip.
 */
export const POSITIONS_UNSETTLED =
  "Confirm every position in the novelty analysis first";

function Gate({ taskId }: { taskId: string }) {
  // The page's own query, so the guard and the page share one answer.
  const { data } = useClaimStrategy(taskId);
  const writable = useClaimSetWritable(taskId);
  // A stored set is still worth reading while it takes no writes: the page draws it
  // read-only and says why. Only an empty stage has nothing to show.
  const shut = data !== undefined && !writable && data.claims.length === 0;
  // The id keeps a mount effect run twice (StrictMode) to one toast.
  useEffect(() => {
    if (shut) toast.info(POSITIONS_UNSETTLED, { id: "claim-strategy-shut" });
  }, [shut]);
  return shut ? (
    <Navigate to={tabPath(taskId, "thesis", "novelty", "analysis")} replace />
  ) : (
    <Outlet />
  );
}

/**
 * The way onto Claim Strategy. A definite no over an empty stage sends the
 * attorney to the analysis and says why, once; an unread stage lets the page
 * through, since a redirect off nothing would bounce every cold load. Zero
 * props by contract: it reads its own route params.
 */
export function ClaimStrategyGuard() {
  const { taskId } = useParams<{ taskId: string }>();
  return taskId === undefined ? <Outlet /> : <Gate taskId={taskId} />;
}
